/**
 * Festival dates, in the reader's language.
 *
 * The festival's TIME RULES stay in `@myrave/shared/time` — which day an act
 * belongs to, where that day starts, the edition's zone. What this adds is
 * only the last step, the words: `@myrave/shared` spells days and months in
 * English (`"Fri 31 Jul"`), and a Hebrew screen needs `"יום ו׳, 31 ביולי"`.
 *
 * Each helper takes the English the shared formatter already produced and
 * returns it untouched for English, so an English screen is byte-for-byte what
 * it was. Every other locale goes through `Intl`, always in the EDITION's zone
 * — never the phone's, which is the whole point of the shared rules.
 *
 * No `@myrave/shared` import on purpose: `src/ui` also builds on its own
 * (`tsconfig.ui.json`), and the caller already has the instant.
 */

/*
 * Bare `en` is the shared formatter's own English, byte-for-byte. A full tag
 * (`en-US`, `en-IL`, `he-IL` — step 73) goes through `Intl`, so an American
 * reader gets "Fri, Jul 31" and an Israeli one "31 Jul".
 */
const isEnglish = (locale: string) => locale === 'en';
/** The tag `Intl` formats with: bare `en` keeps the day-month English this app always had. */
const intlTag = (locale: string) => (locale === 'en' ? 'en-GB' : locale);

/**
 * A festival day — `"Fri 31 Jul"`, or `"Fri 31"` when `short`.
 *
 * `at` is any instant inside that day in `timeZone`; the day's start from
 * `dayStart()` is the natural one to pass.
 */
export function localDay(
  locale: string, english: string, at: Date, timeZone: string, short = false,
): string {
  if (isEnglish(locale)) return short ? english.split(' ').slice(0, 2).join(' ') : english;
  const options: Intl.DateTimeFormatOptions = short
    ? { timeZone, weekday: 'short', day: 'numeric' }
    : { timeZone, weekday: 'short', day: 'numeric', month: 'short' };
  return new Intl.DateTimeFormat(locale, options).format(at);
}

/**
 * A set's weekday in the rave's zone — `"Sat"` — for "Sat 23:00" (What's new, step 83).
 * `at` is the start of its festival DAY (`dayStart`), so a 00:30 set keeps the day it belongs to.
 */
export function localWeekday(locale: string, at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(intlTag(locale), { timeZone, weekday: 'short' }).format(at);
}

/** An edition's date range — `"24 July – 4 August 2026"`. */
export function localDateRange(
  locale: string, english: string, start: Date, end: Date, timeZone: string,
): string {
  if (isEnglish(locale)) return english;
  const format = new Intl.DateTimeFormat(locale, { timeZone, day: 'numeric', month: 'long', year: 'numeric' });
  return start.getTime() === end.getTime() ? format.format(start) : format.formatRange(start, end);
}

/**
 * A moment shown to the reader — "counted at", "printed at", "published".
 *
 * Always in the reader's locale, asked for by name — never the browser's
 * (step 73: English used to take the browser's own default, so a Hebrew
 * browser printed Hebrew dates on an English page; a language chosen in the
 * app must win over the device's, 2026-10-28).
 */
export function localDateTime(locale: string, at: Date): string {
  return at.toLocaleString(intlTag(locale));
}

/** A number, grouped the reader's way. */
export function localNumber(locale: string, n: number): string {
  return n.toLocaleString(intlTag(locale));
}

/** A country by NAME in the reader's language ("HU" → "הונגריה"); anything but a code as it came. */
export function localCountry(locale: string, code: string | null | undefined): string | null {
  if (!code) return null;
  if (!/^[A-Za-z]{2}$/.test(code)) return code;
  try {
    return new Intl.DisplayNames([intlTag(locale)], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}
